// WS-75: an instance's own matrix, readable in a node material (TSL): the one way the map draws many copies of a
// material's geometry. Billboards (tree impostors, branchlet quads), the near trees and per-instance sway place and turn
// each instance in the shader themselves from these columns; the mesh is a plain Mesh made by sharedInstances() below,
// whose matrices are the geometry attributes iM0..iM3 (read by name, so every such mesh of a material shares one node
// build). Columns: iCol(0) = x axis (xyz), iCol(1) = y axis, iCol(2) = z axis, iCol(3) = translation (w = 1).
// Not an InstancedMesh (MAPMYSTAY-VIEWER-22): three's own instancing reads its `instanceMatrix` as 4 more vertex
// attributes (and the colour as one), on top of these columns, for the same matrices: the near trees' program asked for
// 18 of WebGPU's 16 vertex inputs and never built, on every device, so a map whose kit was late or failed had no trees.
import * as THREE from 'three/webgpu';
import { Fn, attribute } from 'three/tsl';

const col = (k) => Fn((builder) => {
  if (!builder.geometry?.attributes?.[`iM${k}`]) throw new Error(`gpu/instance: iCol${k} needs a sharedInstances() mesh (${builder.object?.name || builder.object?.type || 'object'} / ${builder.material?.name || builder.material?.type || 'material'})`);
  return attribute(`iM${k}`, 'vec4');
}, 'vec4')();

export const iCol0 = col(0), iCol1 = col(1), iCol2 = col(2), iCol3 = col(3);

/** WS-75 perf: after a refill of the first `count` instances of a sharedInstances() mesh (matrix, tint, `extra` per-instance
 *  attributes), upload only those: one update range on each (a version bump with no range re-uploads the whole capacity:
 *  1.6 MB per near-tree refill). Call it instead of the needsUpdate flags. Ranges are replaced, never stacked. */
export function markShared(mesh, count, extra = []) {
  const n = Math.max(count, 1);
  const mark = (a, size) => { a.clearUpdateRanges(); a.addUpdateRange(0, Math.min(n * size, a.array.length)); a.needsUpdate = true; };
  const { iM0, iC } = mesh.geometry.attributes;
  mark(iM0.data, 16); // the one interleaved buffer behind iM0..iM3
  mark(iC, 3);
  for (const a of extra) mark(a, a.itemSize);
}

/**
 * WS-75 perf: instances that share ONE node build per material. three r186 keys every InstancedMesh's node build and
 * program by the mesh's uuid (its instance buffers are bound into the build), so 13 forest cells with one material
 * built the same lit program 13 times (+ 12 shadow builds; ~90 ms each at 4x CPU on a phone). This is a plain Mesh
 * with an InstancedBufferGeometry instead: the per-instance data are geometry attributes, bound by name per draw, so
 * meshes with the same material and attribute layout share one build. Per instance: iM0..iM3 = the matrix columns
 * (gpu/instance.js iCol*, one interleaved buffer) and iC = the tint (three does NOT apply it: the material multiplies its
 * colour by attribute('iC') itself). The base geometry's attributes and index are shared, not copied.
 * Returns the mesh; mesh.userData.inst = { matrix, color, count } (the Float32Arrays; set geometry.attributes.iM0.data
 * / iC needsUpdate after writing). Set geometry.boundingBox / boundingSphere to the instances' bounds (culling).
 * Not raycast (the quad at the origin is not where the instances are).
 */
export function sharedInstances(base, material, n) {
  const g = new THREE.InstancedBufferGeometry();
  g.index = base.index;
  for (const [k, a] of Object.entries(base.attributes)) g.setAttribute(k, a);
  g.instanceCount = n;
  const ib = new THREE.InstancedInterleavedBuffer(new Float32Array(Math.max(n, 1) * 16), 16, 1);
  for (let k = 0; k < 4; k++) g.setAttribute(`iM${k}`, new THREE.InterleavedBufferAttribute(ib, 4, k * 4));
  const iC = new THREE.InstancedBufferAttribute(new Float32Array(Math.max(n, 1) * 3).fill(1), 3);
  g.setAttribute('iC', iC);
  const mesh = new THREE.Mesh(g, material);
  mesh.raycast = () => {};
  mesh.userData.inst = { matrix: ib.array, color: iC.array, count: n };
  return mesh;
}
